import { cloneDeep, orderBy } from "lodash"
import Modal from "modal-react-native-web"
import moment from "moment"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import {
  AppState,
  Dimensions,
  Platform,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native"
import { Overlay } from "react-native-elements"
import Icon from "react-native-vector-icons/Feather"
import { useDispatch, useSelector } from "react-redux"
import { useMediaQuery } from "react-responsive"
import { createSelector } from "reselect"

import Base, { Actions, Api, Firebase, MQTT, Order } from "../../base"
import { OfflineBanner } from "../common"
import Alert from "../common/Alert"
import LiveIndicator from "./LiveIndicator"
import OrderDetailsView from "./OrderDetailsView"
import OrdersHeader from "./OrdersHeader"
import OrdersList from "./OrdersList.web"

function headerOptions(navigation) {
  return {
    ...Base.constants.headerOptions(false, "Orders"),
    headerRight: () => (
      <TouchableOpacity
        style={styles.settings}
        onPress={() => {
          navigation.push("Settings")
          Firebase.logEvent("settings")
        }}
      >
        <Icon name="settings" size={19} color="white" />
      </TouchableOpacity>
    ),
    headerLeft: () => <LiveIndicator />,
  }
}

function orderSelector(sortOrder = "desc", status) {
  return createSelector(
    state => {
      const list = !status
        ? state.orders
        : state.orders.filter(order => {
            for (let x of status) {
              if (order.currentStatus === x) return true
            }
            return false
          })
      return orderBy(
        list,
        [item => moment(item.createdAt).valueOf()],
        [sortOrder],
      )
    },
    result => result,
  )
}

const SORT_OPTIONS = [
  { label: "Most recent", value: "desc" },
  { label: "Oldest", value: "asc" },
]

export default function OrdersScreen(props) {
  const dispatch = useDispatch()

  const [refreshingOrders, setRefreshingOrders] = useState(false)
  const [, setIsLandscape] = useState(false)
  const [detailsOrder, setDetailsOrder] = useState(null)
  const [tab, setTab] = useState(0)
  const [sortOrder, setSortOrder] = useState("desc")

  const venue = useSelector(state => state.venue)
  const isFlooid = useMemo(() => venue?.system?.integration === "Flooid", [
    venue,
  ])

  const allOrders = useSelector(orderSelector(sortOrder))

  const newOrders = useSelector(
    orderSelector(sortOrder, [Order.status.PENDING]),
  )
  const currentOrders = useSelector(
    orderSelector(sortOrder, Order.currentGroup),
  )
  const completeOrders = useSelector(
    orderSelector(sortOrder, Order.completedGroup),
  )

  const isMobile = useMediaQuery(Base.constants.mobile)
  const isTablet = useMediaQuery(Base.constants.tablet)

  props.navigation.setOptions(headerOptions(props.navigation))

  useEffect(() => {
    getOrders()
  }, [getOrders])
  const getOrders = useCallback(async () => {
    const orders = await Api.getAllOrders(venue)
    dispatch(Actions.setOrders(orders))
    MQTT.connect(venue._id, onMQTTMEssage)
  }, [dispatch, onMQTTMEssage, venue])

  useEffect(() => {
    const onChange = result => setIsLandscape(result)
    Dimensions.addEventListener("change", onChange)
    return () => Dimensions.removeEventListener("change", onChange)
  })

  useEffect(() => {
    AppState.addEventListener("change", stateChanged)
    return () => {
      AppState.removeEventListener("change", stateChanged)
    }
  }, [stateChanged])

  const stateChanged = useCallback(
    state => {
      if (state === "active") {
        getOrders()
      }
      if (state === "inactive" || state === "background") {
        MQTT.disconnect()
      }
    },
    [getOrders],
  )

  async function pullToRefresh() {
    console.log("REFRESHING")
    setRefreshingOrders(true)
    setTimeout(async function () {
      await getOrders()
      setRefreshingOrders(false)
    }, 1000)
  }

  async function getOrderDetails(order) {
    Firebase.logEvent("order_details")
    setDetailsOrder(order)
    const detOrder = await Api.getOrder(order._id)
    if (detOrder && detOrder.basket) {
      dispatch(Actions.addOrder(detOrder))
      setDetailsOrder(detOrder)
    }
  }

  async function orderPressed(order, action) {
    if (action === "details") {
      getOrderDetails(order)
      return
    }

    Firebase.logEvent("order_status", { nextStatus })
    let nextStatus = Order.statusForAction(order, action)
    if (action === "positive") {
      saveOrderStatus(order, nextStatus)
    } else {
      let purpose =
        order.currentStatus === Order.status.PENDING ? "Reject" : "Cancel"
      Alert.showTwoButton(
        purpose + " Order",
        `\nAre you sure you want to ${purpose.toLowerCase()} this order?`,
        { text: "No, Thanks", onPress: () => {} },
        {
          text: purpose + " Order",
          onPress: () => {
            saveOrderStatus(order, nextStatus)
          },
        },
      )
    }
  }

  async function saveOrderStatus(order, status) {
    if (order.isUpdating) return
    dispatch(Actions.setOrderUpdating(order, true))
    if (detailsOrder) {
      setDetailsOrder({ ...detailsOrder, isUpdating: true })
    }
    let result = await Api.updateOrderStatus(order, status)
    if (result && !result._id) {
      Alert.show(
        "Hold Up!",
        "\nWe were unable to update this order, please check your connection and try again.",
      )
      dispatch(Actions.setOrderUpdating(order, false))
    } else if (result?._id) {
      setDetailsOrder(null)
      dispatch(Actions.updateOrderStatus(order, status))
    }
  }

  const onMQTTMEssage = useCallback(
    async (orderId, action) => {
      const order = await Api.getOrder(orderId)
      if (action === "new_order") {
        if (order && order.basket) {
          dispatch(Actions.addOrder(order))
          Base.functions.showNewOrderMessage(order, () =>
            setDetailsOrder(order),
          )
        }
      } else if (action === "auto_rejected_order") {
        Base.functions.showRejectedMessage(order)
        getOrders()
      }
    },
    [dispatch, getOrders],
  )

  function currentList() {
    if (isFlooid) {
      return (
        <View style={styles.allOrdersList}>{orderList("", allOrders)}</View>
      )
    }
    if (+tab === 2 || (!isMobile && +tab === 1)) {
      return (
        <View style={styles.listContainer}>
          <View style={styles.listWrap}>
            {orderList("", completeOrders, true)}
          </View>
        </View>
      )
    } else if (!isMobile && tab === 0) {
      return (
        <View style={styles.listContainer}>
          {orderList("Pending", newOrders)}
          {orderList("Accepted", currentOrders)}
        </View>
      )
    }
    return orderList("", tab === 0 ? newOrders : currentOrders)
  }

  function dismissDetails() {
    if (detailsOrder && detailsOrder.isUpdating) return
    setDetailsOrder(null)
  }

  function orderList(title, orders, historic = false) {
    return (
      <OrdersList
        orders={orders}
        title={title}
        historic={historic}
        onPress={orderPressed}
        size={{ isMobile, isTablet }}
        onRefresh={pullToRefresh}
        isRefreshing={refreshingOrders}
        isStatusEditable={!isFlooid}
      />
    )
  }

  const sortProps = useMemo(
    () => ({
      options: SORT_OPTIONS,
      selectedValue: sortOrder,
      onChange: setSortOrder,
    }),
    [sortOrder],
  )

  return (
    <View style={styles.main}>
      <OrdersHeader
        tab={tab}
        venue={venue}
        onTabChanged={setTab}
        isMobile={isMobile}
        pendingCount={newOrders.length}
        sortProps={sortProps}
        showTabs={!!venue && !isFlooid}
      />
      {currentList()}
      {Platform.OS === "web" ? (
        <Modal
          animationType="fade"
          transparent={true}
          visible={detailsOrder != null}
          onDismiss={dismissDetails}
        >
          <OrderDetailsView
            order={detailsOrder}
            onClose={dismissDetails}
            onAction={orderPressed}
            isMobile={isMobile}
            isLoading={false}
            isFlooid={isFlooid}
          />
        </Modal>
      ) : (
        <Overlay
          isVisible={detailsOrder != null}
          overlayStyle={styles.overlay}
          borderRadius={4}
          width={!isMobile ? 600 : "90%"}
          height={"90%"}
          windowBackgroundColor="#000000dd"
          animationType={"fade"}
          statusBarTranslucent={"true"}
          onBackdropPress={dismissDetails}
        >
          <OrderDetailsView
            order={detailsOrder}
            onClose={dismissDetails}
            onAction={orderPressed}
            isMobile={isMobile}
            isLoading={false}
            isFlooid={isFlooid}
          />
        </Overlay>
      )}
      <OfflineBanner />
    </View>
  )
}

const styles = StyleSheet.create({
  main: {
    flex: 1,
    display: "flex",
    backgroundColor: Base.colours.appLightGrey,
  },

  allOrdersList: {
    flexGrow: 1,
    alignItems: "center",
    flexDirection: "column",
  },

  listContainer: {
    flex: 1,
    flexDirection: "row",
  },

  listWrap: { flex: 1, alignItems: "center" },

  settings: {
    width: 40,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
  },

  overlay: { padding: 0, shadowOpacity: 0 },
})
